// $FlowFixMe
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useQuery } from 'react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import AffordableFloorPlanPricingService from '../../services/floorPlanPricingService';
import LeaseService from '../../services/leaseService';
import ResidentService from '../../services/residentService';
import { parseLeaseMonthlyReqFees } from '../../utils/lease-helpers';
import { formatProgramList } from '../../utils/affordable';
import { useActiveTransfer } from '../ResidentProfile/ResidentTransferCard/hooks';
import buttonStateMessages from './LeaseDataTabForm/LeaseDataTabFormSections/LeaseActionButtons/ButtonStates/messages';

export const useAsyncFloorPlanPricing = (
  floorplanId: string | null,
  isAffordable: boolean,
  selectedProperty: Object,
) => {
  const [affordableFloorplanPricing, setFloorPlanPricing] = useState();

  useEffect(() => {
    // $FlowFixMe
    const abortController = new AbortController(); // eslint-disable-line
    let subscribed = true;

    const fetchAffordableFloorplanPricings = async () => {
      if (!affordableFloorplanPricing && floorplanId && isAffordable) {
        const { id: propertyId, organizationId } = selectedProperty;
        const pricingService = new AffordableFloorPlanPricingService();
        const options = { signal: abortController.signal };
        const pricingRes = await pricingService.getAll(
          organizationId,
          propertyId,
          floorplanId,
          options,
        );
        setFloorPlanPricing(pricingRes);
      }
    };

    if (subscribed) {
      fetchAffordableFloorplanPricings();
    }

    return () => {
      subscribed = false;
    };
    // ignore [affordableFloorplanPricing]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floorplanId, isAffordable, selectedProperty, setFloorPlanPricing]);

  return affordableFloorplanPricing || {};
};

export const useAsyncMonthlyLeaseRequiredFees = (
  leaseId: string,
  lockedFees: string,
) => {
  const [subscribed, setSubscribed] = useState(true);
  const [fetch, setFetch] = useState(true);
  const [leaseMonthlyRequiredFees, setLeaseMonthlyRequiredFees] =
    useState(null);

  const updateRequiredFeesList = () => setFetch(true);
  const fetchLeaseMonthlyRequiredFees = useCallback(async () => {
    setSubscribed(true);
    if (leaseId || fetch || lockedFees) {
      // $FlowFixMe
      const abortController = new AbortController(); // eslint-disable-line
      const leaseService = new LeaseService();
      const options = { signal: abortController.signal };
      let leaseMonthlyRequiredFees = [];

      leaseMonthlyRequiredFees = leaseId
        ? await leaseService.getMonthlyRequiredFeesForLease(leaseId, options)
        : [];

      if (subscribed) {
        const parsedRequiredFees = parseLeaseMonthlyReqFees(
          leaseMonthlyRequiredFees,
        );
        setLeaseMonthlyRequiredFees(parsedRequiredFees);
        setFetch(false);
      }
    }
  }, [leaseId, fetch, subscribed, lockedFees]);

  useEffect(() => {
    fetchLeaseMonthlyRequiredFees();
    return () => {
      setSubscribed(false);
    };
  }, [fetchLeaseMonthlyRequiredFees]);

  return {
    leaseMonthlyRequiredFees: leaseMonthlyRequiredFees || [],
    updateRequiredFeesList: updateRequiredFeesList,
    requiredFeesUpdated: !fetch,
  };
};

export const useAsyncEditMonthlyRequiredFees = (
  organizationId: string,
  propertyId: string,
  leaseId: string,
  setUpdated: Function,
  toastr: Function,
) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitUpdateMonthlyRequiredFees = async ({
    removedMonthlyFees,
    requiredMonthlyFees,
  }: Object) => {
    setIsSubmitting(true);

    try {
      const leaseService = new LeaseService();
      const response = await leaseService.updateMonthlyRequiredFeesForLease(
        organizationId,
        propertyId,
        leaseId,
        requiredMonthlyFees,
        removedMonthlyFees,
      );
      setIsSubmitting(false);
      setUpdated(true);

      toastr({
        message: 'Non-rent charges Updated',
        title: 'Success',
      });

      if (response.error) {
        return true;
      }

      return true;
    } catch (e) {
      setIsSubmitting(false);
      toastr({
        message: `${e.toString()}`,
        title: 'Error',
        type: 'error',
      });
      return false;
    }
  };

  return {
    isSubmitting,
    submitUpdateMonthlyRequiredFees,
  };
};
export const useAsyncHasOpenAffordableQualification = (
  organizationId: string,
  propertyId: string,
  residentId: string,
  isAffordable: boolean,
) => {
  const service = useMemo(() => new ResidentService(), []);
  const query = useQuery(
    ['resident', 'hasOpenAffordableQualification'],
    () =>
      service.getHouseholdHasOpenCerts(
        organizationId,
        propertyId,
        residentId,
        true,
      ),
    {
      enabled: isAffordable,
      refetchInterval: 60000,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
    },
  );

  return {
    isLoading: query.isLoading,
    hasOpenAffordableQualifications:
      query?.data?.hasNonGrossRentOpenCert ?? false,
    hasOpenGrossRentCertification: query?.data?.hasGrossRentOpenCert ?? false,
  };
};

/**
 * ENG-31662 — whether this transfer lease is blocked from execution because any of its transfer
 * certifications is still unapproved, plus the tooltip copy naming the programs. Every affordable
 * program on the transfer gates execution, not just HUD and RD.
 *
 * Wraps the existing `useActiveTransfer` rather than fetching again: same React Query key as the
 * Resident Transfer Card, so there is one request and one cache, and that hook already handles the
 * refresh story (staleTime 0, refetchOnMount 'always', invalidation on the LEASE_* window events).
 * That is what re-disables the buttons when a certification leaves an approved status (Sc3).
 */
export const useTransferLeaseCertGate = ({
  lease,
  householdId,
  selectedProperty,
  intl,
}: Object) => {
  const { unitTransfers: unitTransfersFlag } = useFlags();
  const organizationId = selectedProperty?.organizationId ?? '';
  const propertyId = selectedProperty?.id ?? '';
  const enabled = Boolean(unitTransfersFlag && lease?.isTransfer && lease?.id);

  const { data } = useActiveTransfer(
    organizationId,
    propertyId,
    householdId,
    enabled,
  );

  const describesThisLease =
    enabled && data?.details?.transferLeaseId === lease?.id;
  const blockedPrograms = describesThisLease
    ? data?.leaseExecutionBlockedPrograms ?? []
    : [];
  const isBlocked = blockedPrograms.length > 0;

  return {
    isBlocked,
    blockedPrograms,
    tooltipTitle: isBlocked
      ? intl.formatMessage(buttonStateMessages.transferCertNotApproved, {
          programs: formatProgramList(blockedPrograms),
        })
      : '',
  };
};

/**
 * ENG-32187 / ENG-32886 — the applicant/move-in gate's tooltip copy, built once here (beside the
 * transfer gate above) so both method-button components consume a ready string instead of each
 * formatting the program list. The gate blocks generate + execute — never Save — when an active
 * MOVE_IN cert is unapproved. It shares the affordable compliance gate's wording (no "transfer");
 * the generic fallback keeps the copy off a bare "There is an open certification" when the gate
 * fires with no nameable program. `unapprovedMoveInPrograms` are the tab-scoped names from
 * ApplicationProfile, so a phantom cert mapping to no approvable tab neither pads nor blocks.
 */
export const buildMoveInCertTooltip = (
  intl: Object,
  unapprovedMoveInPrograms: Array<string> = [],
): string => {
  // Branch on the FORMATTED string, not the raw length: formatProgramList drops empty/whitespace
  // names, so a `['']` list must fall through to the generic copy rather than interpolating a
  // blank program into "There is an open  certification".
  const programs = formatProgramList(unapprovedMoveInPrograms);
  return programs
    ? intl.formatMessage(buttonStateMessages.certificationsNotApproved, {
        programs,
      })
    : intl.formatMessage(buttonStateMessages.certificationsNotApprovedGeneric);
};

/**
 * ENG-32168 — whether the Start Lease Renewal button must be shown-but-disabled because the
 * household has an active (IN_PROGRESS) unit transfer. Gated on the `unitTransfers` flag so
 * flag-off behavior is byte-identical to today (Sc5). Reuses `useActiveTransfer` — the same React
 * Query key the Resident Transfer Card and the cert gate use — so there is one request/cache and
 * the shared LEASE_* invalidation re-enables the button the moment the transfer completes or is
 * canceled (`getActiveTransfer` returns null once it leaves IN_PROGRESS), satisfying Sc2/Sc3.
 *
 * While the query is still loading (`data === undefined`) the pessimistic `loadingFallback` — the
 * household's synchronous "has a transfer lease" signal — governs so the button never flashes
 * enabled during an active transfer (Sc1). Once the query resolves, `data` wins: a `null` after
 * completion/cancellation overrides the stale lease selector and re-enables the button (Sc2/Sc3).
 */
export const useRenewalBlockedByActiveTransfer = ({
  isResident,
  householdId,
  selectedProperty,
  loadingFallback = false,
}: Object) => {
  const { unitTransfers: unitTransfersFlag } = useFlags();
  const organizationId = selectedProperty?.organizationId ?? '';
  const propertyId = selectedProperty?.id ?? '';
  const enabled = Boolean(unitTransfersFlag && isResident && householdId);

  const { data } = useActiveTransfer(
    organizationId,
    propertyId,
    householdId,
    enabled,
  );

  if (!enabled) return false;
  return data !== undefined ? Boolean(data) : Boolean(loadingFallback);
};
